
import { unzipSync, strFromU8, type Unzipped } from "fflate";
import { resolveThemeChain } from "./ooxmlPaths";
import { EMU_PER_POINT, NS_A, NS_P, NS_R } from "../../../const";
import { applyLumMod, hexToRgb, rgbToHex } from "../../../utils/colourUtils";
import { getActivePresentationBuffer } from "../../../utils/officePptx";

/** What cell to match in the OOXML (the live selection's geometry + grid). */
export interface TableFillMatch {
  leftPt: number;
  topPt: number;
  rowCount: number;
  colCount: number;
}

/** Effective fill hex (no '#') per cell, or null when none/unresolvable. Indexed [r][c]. */
export type CellFillGrid = Array<Array<string | null>>;

type ThemeMap = Record<string, string>; // slot → hex (dk1, lt1, accent1, …)
type ClrMap = Record<string, string>; // bg1/tx1/bg2/tx2 → theme slot

interface TableFlags {
  firstRow: boolean;
  lastRow: boolean;
  firstCol: boolean;
  lastCol: boolean;
  bandRow: boolean;
  bandCol: boolean;
}

export async function resolveTableCellFills(match: TableFillMatch): Promise<CellFillGrid> {
  const empty = emptyGrid(match.rowCount, match.colCount);
  try {
    const buf = await getActivePresentationBuffer();
    const zip = unzipSync(new Uint8Array(buf));
    const slideIndex = await getActiveSlideIndex();
    const slidePath = locateSlidePath(zip, slideIndex);
    if (!slidePath) return empty;
    const slideDoc = parseXml(zip, slidePath);
    if (!slideDoc) return empty;

    const tbl = findTableAt(slideDoc, match.leftPt, match.topPt);
    if (!tbl) return empty;

    const chain = resolveChain(zip, slidePath);
    const theme = chain.themePath ? readTheme(zip, chain.themePath) : {};
    const clrMap = chain.masterPath ? readClrMap(zip, chain.masterPath) : {};

    const tblPr = directChild(tbl, "tblPr");
    const flags = readFlags(tblPr);
    const styleId = tblPr ? textOfChild(tblPr, "tableStyleId") : null;
    const styleMap = styleId ? readTableStyle(zip, styleId, theme, clrMap) : null;

    const ctx: CellStyleCtx = { match, flags, styleMap, theme, clrMap };
    const rows = childrenNS(tbl, "tr");
    const grid: CellFillGrid = [];
    for (let r = 0; r < match.rowCount; r++) {
      const cellEls = rows[r] ? childrenNS(rows[r]!, "tc") : [];
      const out: Array<string | null> = [];
      for (let c = 0; c < match.colCount; c++) {
        out.push(resolveCellFill(cellEls[c] ?? null, r, c, ctx));
      }
      grid.push(out);
    }
    return grid;
  } catch {
    return empty;
  }
}

/* ── Per-cell resolution ────────────────────────────────────────── */
// Convention for the resolvers below: `null` means "no value present / couldn't
// be resolved from the OOXML" — an expected outcome (the cell simply has no
// explicit fill, the style part is missing, etc.), NOT an error. Callers treat
// null as "leave the default / try the next source".

/** The table-level styling context shared by every cell (bundled to keep the
 * per-cell resolver's parameter list small). */
interface CellStyleCtx {
  match: TableFillMatch;
  flags: TableFlags;
  styleMap: Map<string, string | null> | null;
  theme: ThemeMap;
  clrMap: ClrMap;
}

/** The horizontal-banding style part for row `r`, or null when it doesn't band. */
function bandRowPart(r: number, lastRow: number, flags: TableFlags): string | null {
  if (!flags.bandRow) return null;
  if (r === 0 && flags.firstRow) return null;
  if (r === lastRow && flags.lastRow) return null;
  const bodyIdx = r - (flags.firstRow ? 1 : 0);
  if (bodyIdx < 0) return null;
  return bodyIdx % 2 === 0 ? "band1H" : "band2H";
}

/** The vertical-banding style part for column `c`, or null when it doesn't band. */
function bandColPart(c: number, lastCol: number, flags: TableFlags): string | null {
  if (!flags.bandCol) return null;
  if (c === 0 && flags.firstCol) return null;
  if (c === lastCol && flags.lastCol) return null;
  const bodyIdx = c - (flags.firstCol ? 1 : 0);
  if (bodyIdx < 0) return null;
  return bodyIdx % 2 === 0 ? "band1V" : "band2V";
}

/** Style-part names to try for cell (r,c), highest precedence first. */
function cellStyleOrder(r: number, c: number, match: TableFillMatch, flags: TableFlags): string[] {
  const lastRow = match.rowCount - 1;
  const lastCol = match.colCount - 1;
  const order: string[] = [];
  if (r === 0 && flags.firstRow) order.push("firstRow");
  if (r === lastRow && flags.lastRow) order.push("lastRow");
  if (c === 0 && flags.firstCol) order.push("firstCol");
  if (c === lastCol && flags.lastCol) order.push("lastCol");
  const bandH = bandRowPart(r, lastRow, flags);
  if (bandH) order.push(bandH);
  const bandV = bandColPart(c, lastCol, flags);
  if (bandV) order.push(bandV);
  order.push("wholeTbl");
  return order;
}

function resolveCellFill(cellEl: Element | null, r: number, c: number, ctx: CellStyleCtx): string | null {
  // 1) Explicit cell fill wins outright.
  if (cellEl) {
    const tcPr = directChild(cellEl, "tcPr");
    if (tcPr) {
      const explicit = readContainerFill(tcPr, ctx.theme, ctx.clrMap);
      if (explicit !== undefined) return explicit;
    }
  }
  if (!ctx.styleMap) return null;

  // 2) Otherwise walk the style parts by precedence (highest first).
  for (const part of cellStyleOrder(r, c, ctx.match, ctx.flags)) {
    if (ctx.styleMap.has(part)) return ctx.styleMap.get(part)!;
  }
  return null;
}

/* ── Table + table-style XML ────────────────────────────────────── */
// As above, these lookups return `null` to mean "not found in the OOXML" — an
// expected result (no matching table/part), not an error; callers fall back.

function findTableAt(slideDoc: Document, leftPt: number, topPt: number): Element | null {
  const frames = slideDoc.getElementsByTagNameNS(NS_P, "graphicFrame");
  let firstTbl: Element | null = null;
  for (const frame of Array.from(frames)) {
    const tbl = frame.getElementsByTagNameNS(NS_A, "tbl")[0];
    if (!tbl) continue;
    firstTbl ??= tbl;
    const off = frame.getElementsByTagNameNS(NS_A, "off")[0];
    if (!off) continue;
    const x = Number.parseInt(off.getAttribute("x") ?? "", 10) / EMU_PER_POINT;
    const y = Number.parseInt(off.getAttribute("y") ?? "", 10) / EMU_PER_POINT;
    if (Math.abs(x - leftPt) <= 2 && Math.abs(y - topPt) <= 2) return tbl;
  }
  return firstTbl;
}

function readFlags(tblPr: Element | null): TableFlags {
  const on = (attr: string): boolean => tblPr?.getAttribute(attr) === "1";
  return {
    firstRow: on("firstRow"),
    lastRow: on("lastRow"),
    firstCol: on("firstCol"),
    lastCol: on("lastCol"),
    bandRow: on("bandRow"),
    bandCol: on("bandCol"),
  };
}

const STYLE_PARTS = [
  "wholeTbl",
  "band1H",
  "band2H",
  "band1V",
  "band2V",
  "firstRow",
  "lastRow",
  "firstCol",
  "lastCol",
] as const;

/** Map of style-part → resolved fill (hex, or null for explicit no-fill). */
function readTableStyle(zip: Unzipped, styleId: string, theme: ThemeMap, clrMap: ClrMap): Map<string, string | null> | null {
  const doc = parseXml(zip, "ppt/tableStyles.xml");
  if (!doc) return null;
  const styles = doc.getElementsByTagNameNS(NS_A, "tblStyle");
  const style = Array.from(styles).find((s) => s.getAttribute("styleId") === styleId);
  if (!style) return null;

  const map = new Map<string, string | null>();
  for (const part of STYLE_PARTS) {
    const partEl = directChild(style, part);
    if (!partEl) continue;
    const tcStyle = directChild(partEl, "tcStyle");
    const fillWrap = tcStyle ? directChild(tcStyle, "fill") : null;
    if (!fillWrap) continue;
    const fill = readContainerFill(fillWrap, theme, clrMap);
    if (fill !== undefined) map.set(part, fill);
  }
  return map;
}

function readContainerFill(container: Element, theme: ThemeMap, clrMap: ClrMap): string | null | undefined {
  const solid = directChild(container, "solidFill");
  if (solid) {
    const clr = firstElement(solid);
    return clr ? resolveColor(clr, theme, clrMap) : null;
  }
  if (directChild(container, "noFill")) return null;
  return undefined;
}

/* ── Colour resolution ──────────────────────────────────────────── */

function resolveColor(clrEl: Element, theme: ThemeMap, clrMap: ClrMap): string | null {
  let base: string | null = null;
  if (clrEl.localName === "srgbClr") base = clrEl.getAttribute("val");
  else if (clrEl.localName === "sysClr") base = clrEl.getAttribute("lastClr") ?? clrEl.getAttribute("val");
  else if (clrEl.localName === "schemeClr") base = resolveScheme(clrEl.getAttribute("val"), theme, clrMap);
  if (!base) return null;
  return applyMods(base, clrEl);
}

function resolveScheme(val: string | null, theme: ThemeMap, clrMap: ClrMap): string | null {
  if (!val || val === "phClr") return null;
  const defaults: ClrMap = { bg1: "lt1", tx1: "dk1", bg2: "lt2", tx2: "dk2" };
  const slot = val in defaults ? (clrMap[val] ?? defaults[val]!) : val;
  return theme[slot] ?? null;
}

/** Apply tint / shade / lumMod / lumOff children of a colour element. */
function applyMods(hex: string, clrEl: Element): string {
  let out = normalizeHex(hex);
  const lumMod = childVal(clrEl, "lumMod");
  const lumOff = childVal(clrEl, "lumOff");
  if (lumMod !== null || lumOff !== null) {
    out = applyLumMod(out, (lumMod ?? 100000) / 100000, (lumOff ?? 0) / 100000).replace("#", "");
  }
  const tint = childVal(clrEl, "tint");
  if (tint !== null) out = blend(out, tint / 100000, 255);
  const shade = childVal(clrEl, "shade");
  if (shade !== null) out = blend(out, shade / 100000, 0);
  return out.toUpperCase();
}

/** Blend each channel toward `target` (255=white for tint, 0=black for shade), keeping `keep` of the original. */
function blend(hex: string, keep: number, target: number): string {
  const { r, g, b } = hexToRgb(`#${normalizeHex(hex)}`);
  const mix = (ch: number): number => Math.round(ch * keep + target * (1 - keep));
  return rgbToHex(mix(r), mix(g), mix(b)).replace("#", "");
}

function normalizeHex(hex: string): string {
  return hex.replace("#", "").trim();
}

/* ── Theme + colour map ─────────────────────────────────────────── */

const THEME_SLOTS = ["dk1", "lt1", "dk2", "lt2", "accent1", "accent2", "accent3", "accent4", "accent5", "accent6", "hlink", "folHlink"];

function readTheme(zip: Unzipped, themePath: string): ThemeMap {
  const doc = parseXml(zip, themePath);
  const scheme = doc?.getElementsByTagNameNS(NS_A, "clrScheme")[0];
  const out: ThemeMap = {};
  if (!scheme) return out;
  for (const slot of THEME_SLOTS) {
    const el = directChild(scheme, slot);
    const clr = el ? firstElement(el) : null;
    if (!clr) continue;
    const hex =
      clr.localName === "sysClr"
        ? (clr.getAttribute("lastClr") ?? clr.getAttribute("val"))
        : clr.getAttribute("val");
    if (hex) out[slot] = hex.toUpperCase();
  }
  return out;
}

function readClrMap(zip: Unzipped, masterPath: string): ClrMap {
  const doc = parseXml(zip, masterPath);
  const map = doc?.getElementsByTagNameNS(NS_P, "clrMap")[0];
  const out: ClrMap = {};
  if (!map) return out;
  for (const key of ["bg1", "tx1", "bg2", "tx2"]) {
    const v = map.getAttribute(key);
    if (v) out[key] = v;
  }
  return out;
}

/* ── Rels chain (slide → layout → master → theme) ───────────────── */

function resolveChain(zip: Unzipped, slidePath: string): { masterPath: string | null; themePath: string | null } {
  const { masterPath, themePath } = resolveThemeChain(zip, slidePath.split("/").pop()!);
  return { masterPath, themePath };
}

/* ── Slide location + active index ──────────────────────────────── */

function locateSlidePath(zip: Unzipped, slideIndex: number): string | null {
  const presDoc = parseXml(zip, "ppt/presentation.xml");
  if (!presDoc) return null;
  const sldIds = presDoc.getElementsByTagNameNS(NS_P, "sldId");
  const target = sldIds[slideIndex - 1];
  if (!target) return null;
  const rId = target.getAttributeNS(NS_R, "id") ?? target.getAttribute("r:id");
  if (!rId) return null;

  const relsDoc = parseXml(zip, "ppt/_rels/presentation.xml.rels");
  if (!relsDoc) return null;
  for (const rel of Array.from(relsDoc.getElementsByTagName("Relationship"))) {
    if (rel.getAttribute("Id") === rId) return "ppt/" + rel.getAttribute("Target");
  }
  return null;
}

/** 1-based index of the active slide via the Office common API (defaults to 1). */
function getActiveSlideIndex(): Promise<number> {
  return new Promise((resolve) => {
    try {
      Office.context.document.getSelectedDataAsync(Office.CoercionType.SlideRange, (res) => {
        const slides = (res.value as { slides?: Array<{ index: number }> } | undefined)?.slides;
        resolve(slides?.[0]?.index ?? 1);
      });
    } catch {
      resolve(1);
    }
  });
}

/* ── DOM helpers ────────────────────────────────────────────────── */

function parseXml(zip: Unzipped, path: string): Document | null {
  const file = zip[path];
  if (!file) return null;
  const doc = new DOMParser().parseFromString(strFromU8(file), "text/xml");
  return doc.getElementsByTagName("parsererror").length > 0 ? null : doc;
}

/** First direct child element with the DrawingML namespace and the given local name. */
function directChild(parent: Element | null, local: string): Element | null {
  if (!parent) return null;
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === 1) {
      const el = node as Element;
      if (el.namespaceURI === NS_A && el.localName === local) return el;
    }
  }
  return null;
}

/** All direct child elements (DrawingML namespace) with the given local name. */
function childrenNS(parent: Element, local: string): Element[] {
  const out: Element[] = [];
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === 1) {
      const el = node as Element;
      if (el.namespaceURI === NS_A && el.localName === local) out.push(el);
    }
  }
  return out;
}

function firstElement(parent: Element): Element | null {
  for (const node of Array.from(parent.childNodes)) {
    if (node.nodeType === 1) return node as Element;
  }
  return null;
}

function textOfChild(parent: Element, local: string): string | null {
  return directChild(parent, local)?.textContent?.trim() || null;
}

function childVal(parent: Element, local: string): number | null {
  const el = directChild(parent, local);
  const v = el?.getAttribute("val");
  return v === null || v === undefined ? null : Number.parseInt(v, 10);
}

function emptyGrid(rows: number, cols: number): CellFillGrid {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => null));
}
